import { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { useNavigate } from 'react-router-dom';
import { 
  CalendarDays, 
  DollarSign, 
  Clock, 
  TrendingUp,
  TrendingDown,
  Castle,
  AlertCircle,
  XCircle,
  RefreshCw,
  CalendarCheck,
  CalendarX,
  Loader2,
  Plus,
  FileText,
  ArrowRight
} from 'lucide-react';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';
import { Button } from '../../components/ui/button';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const statusColors = {
  reservado: 'bg-blue-100 text-blue-800',
  liquidado: 'bg-green-100 text-green-800',
  completado: 'bg-gray-100 text-gray-800',
  cancelado: 'bg-red-100 text-red-800',
};

export default function DashboardPage() {
  const { getAuthHeader } = useAuth();
  const navigate = useNavigate();
  const [dashboard, setDashboard] = useState(null);
  const [loading, setLoading] = useState(true);
  const [calendarStatus, setCalendarStatus] = useState(null);
  const [calendarLoading, setCalendarLoading] = useState(false);
  const [syncLoading, setSyncLoading] = useState(false);

  useEffect(() => {
    const fetchDashboard = async () => {
      try {
        const dashRes = await axios.get(`${API_URL}/dashboard`, { headers: getAuthHeader() });
        setDashboard(dashRes.data);
      } catch (error) {
        console.error('Error fetching dashboard:', error);
      }
      try {
        const calRes = await axios.get(`${API_URL}/oauth/calendar/status`, { headers: getAuthHeader() });
        setCalendarStatus(calRes.data);
      } catch (error) {
        setCalendarStatus({ connected: false });
      }
      setLoading(false);
    };
    fetchDashboard();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const connectGoogleCalendar = async () => {
    setCalendarLoading(true);
    try {
      const res = await axios.get(`${API_URL}/oauth/calendar/login`, { headers: getAuthHeader() });
      window.location.href = res.data.authorization_url;
    } catch (error) {
      console.error('Error connecting Google Calendar:', error);
      setCalendarLoading(false);
    }
  };

  const disconnectGoogleCalendar = async () => {
    try {
      await axios.post(`${API_URL}/oauth/calendar/disconnect`, {}, { headers: getAuthHeader() });
      setCalendarStatus({ connected: false });
    } catch (error) {
      console.error('Error disconnecting:', error);
    }
  };

  const syncAllRentas = async () => {
    setSyncLoading(true);
    try {
      const res = await axios.post(`${API_URL}/oauth/calendar/sync-all`, {}, { headers: getAuthHeader() });
      alert(res.data.message);
    } catch (error) {
      console.error('Error syncing:', error);
    } finally {
      setSyncLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-primary border-t-transparent"></div>
      </div>
    );
  }

  return (
    <div className="space-y-4 sm:space-y-6" data-testid="dashboard-page">
      {/* Header + Quick Actions */}
      <div className="flex flex-col gap-3">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="font-heading text-2xl sm:text-3xl font-bold text-allegra-dark" data-testid="dashboard-title">Dashboard</h1>
            <p className="text-xs sm:text-sm text-allegra-gray">
              {format(new Date(), "d 'de' MMMM, yyyy", { locale: es })}
            </p>
          </div>
          <Badge variant="outline" className="text-xs sm:text-sm hidden sm:inline-flex">
            Resumen del mes
          </Badge>
        </div>

        {/* Quick Actions - prominent on mobile */}
        <div className="flex gap-2" data-testid="quick-actions">
          <Button 
            onClick={() => navigate('/admin/rentas')} 
            className="flex-1 sm:flex-none gap-1.5 rounded-xl h-10 text-xs sm:text-sm"
            data-testid="quick-nueva-renta"
          >
            <Plus className="w-4 h-4" />
            Nueva Renta
          </Button>
          <Button 
            variant="outline" 
            onClick={() => navigate('/admin/cotizaciones')} 
            className="flex-1 sm:flex-none gap-1.5 rounded-xl h-10 text-xs sm:text-sm"
            data-testid="quick-nueva-cotizacion"
          >
            <FileText className="w-4 h-4" />
            Cotizar
          </Button>
          <Button 
            variant="outline" 
            onClick={() => navigate('/admin/calendario')} 
            className="flex-1 sm:flex-none gap-1.5 rounded-xl h-10 text-xs sm:text-sm"
            data-testid="quick-calendario"
          >
            <CalendarDays className="w-4 h-4" />
            <span className="hidden sm:inline">Calendario</span>
            <span className="sm:hidden">Cal.</span>
          </Button>
        </div>
      </div>

      {/* Main KPIs - 2x2 grid */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4" data-testid="main-kpis">
        <Card className="border-0 shadow-lg bg-white" data-testid="kpi-rentas-hoy">
          <CardContent className="p-3.5 sm:p-6">
            <div className="flex items-start justify-between">
              <div className="min-w-0 space-y-1">
                <p className="text-[11px] sm:text-sm text-allegra-gray font-medium uppercase tracking-wide">Rentas Hoy</p>
                <p className="font-heading text-2xl sm:text-3xl font-bold text-allegra-dark">
                  {dashboard?.rentas_hoy || 0}
                </p>
              </div>
              <div className="w-9 h-9 sm:w-11 sm:h-11 rounded-xl bg-primary/10 flex items-center justify-center flex-shrink-0">
                <CalendarDays className="w-4 h-4 sm:w-5 sm:h-5 text-primary" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-0 shadow-lg bg-white" data-testid="kpi-ingresos-mes">
          <CardContent className="p-3.5 sm:p-6">
            <div className="flex items-start justify-between">
              <div className="min-w-0 space-y-1">
                <p className="text-[11px] sm:text-sm text-allegra-gray font-medium uppercase tracking-wide">Ingresos</p>
                <p className="font-heading text-xl sm:text-3xl font-bold text-green-600 truncate">
                  ${dashboard?.ingresos_mes?.toLocaleString() || 0}
                </p>
              </div>
              <div className="w-9 h-9 sm:w-11 sm:h-11 rounded-xl bg-green-100 flex items-center justify-center flex-shrink-0">
                <TrendingUp className="w-4 h-4 sm:w-5 sm:h-5 text-green-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-0 shadow-lg bg-white" data-testid="kpi-egresos-mes">
          <CardContent className="p-3.5 sm:p-6">
            <div className="flex items-start justify-between">
              <div className="min-w-0 space-y-1">
                <p className="text-[11px] sm:text-sm text-allegra-gray font-medium uppercase tracking-wide">Egresos</p>
                <p className="font-heading text-xl sm:text-3xl font-bold text-red-500 truncate">
                  ${dashboard?.egresos_mes?.toLocaleString() || 0}
                </p>
              </div>
              <div className="w-9 h-9 sm:w-11 sm:h-11 rounded-xl bg-red-100 flex items-center justify-center flex-shrink-0">
                <TrendingDown className="w-4 h-4 sm:w-5 sm:h-5 text-red-500" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-0 shadow-lg bg-white" data-testid="kpi-balance">
          <CardContent className="p-3.5 sm:p-6">
            <div className="flex items-start justify-between">
              <div className="min-w-0 space-y-1">
                <p className="text-[11px] sm:text-sm text-allegra-gray font-medium uppercase tracking-wide">Balance</p>
                <p className={`font-heading text-xl sm:text-3xl font-bold truncate ${(dashboard?.balance_mes || 0) >= 0 ? 'text-blue-600' : 'text-red-600'}`}>
                  ${dashboard?.balance_mes?.toLocaleString() || 0}
                </p>
              </div>
              <div className={`w-9 h-9 sm:w-11 sm:h-11 rounded-xl flex items-center justify-center flex-shrink-0 ${(dashboard?.balance_mes || 0) >= 0 ? 'bg-blue-100' : 'bg-red-100'}`}>
                <DollarSign className={`w-4 h-4 sm:w-5 sm:h-5 ${(dashboard?.balance_mes || 0) >= 0 ? 'text-blue-600' : 'text-red-600'}`} />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Secondary Stats Row */}
      <div className="grid grid-cols-3 gap-2.5 sm:gap-4" data-testid="secondary-kpis">
        <Card className="border-0 shadow-md" data-testid="kpi-pendiente">
          <CardContent className="p-3 sm:p-5 text-center">
            <AlertCircle className="w-5 h-5 text-amber-500 mx-auto mb-1" />
            <p className="font-heading text-lg sm:text-xl font-bold text-amber-600 truncate">
              ${dashboard?.total_pendiente?.toLocaleString() || 0}
            </p>
            <p className="text-[10px] sm:text-xs text-allegra-gray mt-0.5">Por Cobrar</p>
          </CardContent>
        </Card>

        <Card className="border-0 shadow-md" data-testid="kpi-rentas-mes">
          <CardContent className="p-3 sm:p-5 text-center">
            <Castle className="w-5 h-5 text-primary mx-auto mb-1" />
            <p className="font-heading text-lg sm:text-xl font-bold text-allegra-dark">
              {dashboard?.rentas_mes || 0}
            </p>
            <p className="text-[10px] sm:text-xs text-allegra-gray mt-0.5">Rentas Mes</p>
          </CardContent>
        </Card>

        <Card className="border-0 shadow-md" data-testid="kpi-cancelaciones">
          <CardContent className="p-3 sm:p-5 text-center">
            <XCircle className="w-5 h-5 text-red-400 mx-auto mb-1" />
            <p className="font-heading text-lg sm:text-xl font-bold text-red-500">
              {dashboard?.cancelaciones_mes || 0}
            </p>
            <p className="text-[10px] sm:text-xs text-allegra-gray mt-0.5">Cancelaciones</p>
          </CardContent>
        </Card>
      </div>

      {/* Proximas Rentas & Top Inflable */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-3 sm:gap-6">
        <Card className="border-0 shadow-lg" data-testid="proximas-rentas">
          <CardHeader className="pb-1 sm:pb-3 px-4 sm:px-6 pt-4 sm:pt-6">
            <div className="flex items-center justify-between">
              <CardTitle className="font-heading text-sm sm:text-lg flex items-center gap-2">
                <Clock className="w-4 h-4 text-primary" />
                Proximas Rentas
              </CardTitle>
              <Button 
                variant="ghost" 
                size="sm" 
                onClick={() => navigate('/admin/rentas')}
                className="text-xs text-primary h-7 px-2"
                data-testid="ver-todas-rentas"
              >
                Ver todas <ArrowRight className="w-3 h-3 ml-1" />
              </Button>
            </div>
          </CardHeader>
          <CardContent className="px-4 sm:px-6 pb-4 sm:pb-6">
            {dashboard?.proximas_rentas?.length > 0 ? (
              <div className="space-y-2">
                {dashboard.proximas_rentas.map((renta) => (
                  <div key={renta.id} className="flex items-center justify-between p-2.5 sm:p-3 bg-muted/60 rounded-xl gap-2" data-testid={`proxima-renta-${renta.id}`}>
                    <div className="flex items-center gap-2.5 min-w-0">
                      <div className="w-9 h-9 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
                        <Castle className="w-4 h-4 text-primary" />
                      </div>
                      <div className="min-w-0">
                        <p className="font-medium text-sm text-allegra-dark truncate">{renta.cliente_nombre || renta.folio}</p>
                        <p className="text-xs text-allegra-gray">
                          {format(new Date(renta.fecha + 'T12:00:00'), "d MMM", { locale: es })} - {renta.hora_inicio}
                        </p>
                      </div>
                    </div>
                    <div className="text-right flex-shrink-0">
                      <Badge className={`text-[10px] sm:text-xs ${statusColors[renta.estatus]}`}>
                        {renta.estatus}
                      </Badge>
                      {renta.saldo_pendiente > 0 && (
                        <p className="text-[10px] sm:text-xs text-amber-600 mt-0.5 font-medium">
                          Debe: ${renta.saldo_pendiente?.toLocaleString()}
                        </p>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-6">
                <CalendarDays className="w-8 h-8 text-allegra-gray/40 mx-auto mb-2" />
                <p className="text-allegra-gray text-sm">No hay rentas proximas</p>
              </div>
            )}
          </CardContent>
        </Card>

        <Card className="border-0 shadow-lg" data-testid="top-inflable">
          <CardHeader className="pb-1 sm:pb-3 px-4 sm:px-6 pt-4 sm:pt-6">
            <CardTitle className="font-heading text-sm sm:text-lg flex items-center gap-2">
              <Castle className="w-4 h-4 text-accent" />
              Inflable Mas Popular
            </CardTitle>
          </CardHeader>
          <CardContent className="px-4 sm:px-6 pb-4 sm:pb-6">
            {dashboard?.top_inflable ? (
              <div className="text-center py-3 sm:py-6">
                <div className="w-14 h-14 sm:w-20 sm:h-20 rounded-2xl bg-gradient-to-br from-primary to-accent flex items-center justify-center mx-auto mb-2 sm:mb-3">
                  <Castle className="w-7 h-7 sm:w-10 sm:h-10 text-white" />
                </div>
                <p className="font-heading text-base sm:text-2xl font-bold text-allegra-dark mb-0.5">
                  {dashboard.top_inflable.nombre}
                </p>
                <p className="text-xs sm:text-sm text-allegra-gray">
                  <strong className="text-primary">{dashboard.top_inflable.rentas}</strong> rentas totales
                </p>
              </div>
            ) : (
              <div className="text-center py-6">
                <Castle className="w-8 h-8 text-allegra-gray/40 mx-auto mb-2" />
                <p className="text-allegra-gray text-sm">Sin datos disponibles</p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Rentas del Dia */}
      {dashboard?.rentas_hoy_detalle?.length > 0 && (
        <Card className="border-0 shadow-lg" data-testid="rentas-hoy-detalle">
          <CardHeader className="pb-1 sm:pb-3 px-4 sm:px-6 pt-4 sm:pt-6">
            <CardTitle className="font-heading text-sm sm:text-lg flex items-center gap-2">
              <CalendarDays className="w-4 h-4 text-primary" />
              Rentas de Hoy
            </CardTitle>
          </CardHeader>
          <CardContent className="px-4 sm:px-6 pb-4 sm:pb-6">
            {/* Mobile: cards */}
            <div className="sm:hidden space-y-2">
              {dashboard.rentas_hoy_detalle.map((renta) => (
                <div key={renta.id} className="p-3 bg-muted/60 rounded-xl" data-testid={`renta-hoy-mobile-${renta.id}`}>
                  <div className="flex items-center justify-between mb-1.5">
                    <span className="font-mono font-medium text-xs text-allegra-gray">{renta.folio}</span>
                    <Badge className={`text-[10px] ${statusColors[renta.estatus]}`}>{renta.estatus}</Badge>
                  </div>
                  <p className="text-sm font-medium text-allegra-dark">{renta.cliente_nombre || '-'}</p>
                  <div className="flex justify-between items-center mt-1.5">
                    <span className="text-xs text-allegra-gray">{renta.hora_inicio} - {renta.hora_fin}</span>
                    <div className="text-right">
                      <span className="text-sm font-bold text-allegra-dark">${renta.total?.toLocaleString()}</span>
                      {renta.saldo_pendiente > 0 && (
                        <p className="text-[10px] text-amber-600 font-medium">Debe: ${renta.saldo_pendiente?.toLocaleString()}</p>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
            {/* Desktop: table */}
            <div className="hidden sm:block overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-border">
                    <th className="text-left py-3 px-4 text-sm font-medium text-allegra-gray">Folio</th>
                    <th className="text-left py-3 px-4 text-sm font-medium text-allegra-gray">Cliente</th>
                    <th className="text-left py-3 px-4 text-sm font-medium text-allegra-gray">Horario</th>
                    <th className="text-left py-3 px-4 text-sm font-medium text-allegra-gray">Total</th>
                    <th className="text-left py-3 px-4 text-sm font-medium text-allegra-gray">Pendiente</th>
                    <th className="text-left py-3 px-4 text-sm font-medium text-allegra-gray">Estatus</th>
                  </tr>
                </thead>
                <tbody>
                  {dashboard.rentas_hoy_detalle.map((renta) => (
                    <tr key={renta.id} className="border-b border-border last:border-0">
                      <td className="py-3 px-4 font-medium font-mono text-sm">{renta.folio}</td>
                      <td className="py-3 px-4 text-sm">{renta.cliente_nombre || '-'}</td>
                      <td className="py-3 px-4 text-sm text-allegra-gray">{renta.hora_inicio} - {renta.hora_fin}</td>
                      <td className="py-3 px-4 font-medium text-sm">${renta.total?.toLocaleString()}</td>
                      <td className="py-3 px-4 text-amber-600 text-sm">${renta.saldo_pendiente?.toLocaleString()}</td>
                      <td className="py-3 px-4">
                        <Badge className={statusColors[renta.estatus]}>{renta.estatus}</Badge>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Google Calendar + Admin Tools - compact row on mobile */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-3 sm:gap-4">
        <Card className="border-0 shadow-lg" data-testid="google-calendar-card">
          <CardContent className="p-3.5 sm:p-6">
            <div className="flex items-center gap-2 mb-3">
              <CalendarCheck className="w-4 h-4 text-primary" />
              <p className="font-heading text-sm sm:text-base font-semibold text-allegra-dark">Google Calendar</p>
            </div>
            {calendarStatus?.connected ? (
              <div className="space-y-3">
                <div className="flex items-center gap-2.5">
                  <div className="w-8 h-8 rounded-full bg-green-100 flex items-center justify-center flex-shrink-0">
                    <CalendarCheck className="w-3.5 h-3.5 text-green-600" />
                  </div>
                  <div className="min-w-0">
                    <p className="font-medium text-sm text-green-700">Conectado</p>
                    <p className="text-xs text-allegra-gray truncate">{calendarStatus.email}</p>
                  </div>
                </div>
                <div className="flex gap-2">
                  <Button variant="outline" size="sm" onClick={syncAllRentas} disabled={syncLoading} className="flex-1 sm:flex-none gap-1 h-8 text-xs" data-testid="sync-calendar-btn">
                    {syncLoading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />}
                    Sincronizar
                  </Button>
                  <Button variant="ghost" size="sm" onClick={disconnectGoogleCalendar} className="text-red-500 hover:bg-red-50 gap-1 h-8 text-xs" data-testid="disconnect-calendar-btn">
                    <CalendarX className="w-3.5 h-3.5" />
                    <span className="hidden sm:inline">Desconectar</span>
                  </Button>
                </div>
              </div>
            ) : (
              <div className="flex items-center justify-between gap-3">
                <p className="text-allegra-gray text-xs sm:text-sm">
                  Sincroniza rentas con tu calendario
                </p>
                <Button onClick={connectGoogleCalendar} disabled={calendarLoading} size="sm" className="gap-1.5 h-8 text-xs flex-shrink-0" data-testid="connect-calendar-btn">
                  {calendarLoading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <CalendarCheck className="w-3.5 h-3.5" />}
                  Conectar
                </Button>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
